import { formatTimeSpan, formDataToObject, GestCard, getCookie, setSession, UserData_model } from '@shared/library';
import importer from '@shared/library/dist/methods/importer';
import axios from 'axios';
import React from 'react';
import { decodeToken } from 'react-jwt';
import { useNavigate } from 'react-router-dom';
import { languages } from '../../../../language';
import { config } from '../../../../static/config';
import './LogIn.scss';
//#region COMPONENTS
import Alert from '@mui/material/Alert';
import Title from '@shared/library/dist/components/gest/Title/Title';
import Caption from '../../../gest/Caption/Caption';
import CustomButton from '../../../gest/CustomButton/CustomButton';
import CustomCheckbox from '../../../gest/CustomCheckbox/CustomCheckbox';
import CustomInput from '../../../gest/CustomInput/CustomInput';
//#endregion
//MODELS 
import SessionModel from '@shared/library/dist/models/Session_model';
import Datetime from '../../../../helpers/datetime';
import { IChallenge, IToken } from '../_models';
//ICONS
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import { ReactComponent as LogoDark } from '../../../../assets/images/logo_dark.svg';
//CONTEXTS
import { CntxConfig } from '../../../Optisewer';

export const formDataConfig = { headers: { "Content-Type": "multipart/form-data" } };

const LogIn = () => {
    //CONTEXTS USE 
    const cntxConfig = React.useContext(CntxConfig)!;

    //STATIC DATA
    const navigate = useNavigate();
    const lang = languages(cntxConfig.settings?.lang);

    //#region STATES
    const [timer, setTimer] = React.useState(0);
    const twoFactorCredentialsStore = React.useRef<any | null>(null);

    const loginForm = React.useRef<HTMLFormElement>(null);

    //FLAGS
    const [errorAnimation, setErrorAnimation] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [askEmailCode, setAskEmailCode] = React.useState(false);
    const [twofactorChallenge, setTwofactorChallenge] = React.useState<string>();
    // const [error, setError] = React.useState<boolean|string>(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    const saveSession = (token: string) => {
        localStorage.clear();

        const userData = decodeToken<UserData_model>(token);    // decodedToken
        const session: SessionModel = { userData, support: false, cancelApiConnection: axios.CancelToken.source() };
        setSession(cntxConfig, session);
    };

    const loginAPI = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (checkForTimer()) return;

        const creds = formDataToObject<{ username: string; password: string; }>(loginForm.current);

        if (creds?.username && creds?.password && creds.password.length >= 8) {
            twoFactorCredentialsStore.current = null;

            setLoading(true);
            axios.post<IToken | IChallenge>(`${config.api.internal}/Auth/Login`, creds, { withCredentials: true })
                .then(res => {
                    const challenge = (res.data as IChallenge).challenge;

                    if (challenge) {
                        //device registered => 2 factor (wait otp)
                        setAskEmailCode(true);
                        twoFactorCredentialsStore.current = creds;
                        setTwofactorChallenge(challenge);
                    }
                    else {
                        //device already registered => token
                        saveSession((res.data as IToken).token);
                    }
                })
                .catch(() => {
                    animateError();
                    localStorage.clear();
                })
                .finally(() => setLoading(false));
        }
        else {
            animateError();
        }
    };

    const twoFactorAPI = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (twoFactorCredentialsStore.current === null) return;

        let creds = formDataToObject<{ otp: string; }>(loginForm.current);

        if (creds?.otp.length === 6) {
            setLoading(true);
            axios.post<IToken>(`${config.api.internal}/Auth/TwoFactor`, { ...creds, challenge: twofactorChallenge }, { withCredentials: true })
                .then(res => saveSession(res.data.token))
                .catch(() => {
                    animateError();
                    localStorage.clear();
                })
                .finally(() => setLoading(false));
        }
        else {
            animateError();
        }
    };
    //#endregion

    const animateError = () => {
        setErrorAnimation(true);
        setTimeout(() => setErrorAnimation(false), 100);
    };

    const checkForTimer = () => {
        const timer = getCookie("D_TIMER");
        const now = new Date();
        if (timer !== null) {
            const time_number = Number(timer);
            const time = new Date(time_number);
            if (time > now) {
                const seconds = Math.floor(time_number / 1000) - new Datetime().getUnixTimestamp();
                setTimer(seconds);
                return true;
            }
        }
        return false;
    };
    //#endregion

    //#region STATES CHANGE
    React.useEffect(() => { checkForTimer(); }, [errorAnimation]);

    React.useEffect(() => {
        if (timer > 0) {
            setTimeout(() => setTimer(timer - 1), 1000);
        }
    }, [timer]);
    //#endregion

    return (
        <GestCard type="floating" id="Login" style={{ width: '350px' }}>
            <div className="logo">
                <LogoDark className={`mb-3 ${errorAnimation ? 'shake' : ''} `} />
                <h4 className='mb-0'>{lang.log_in_into_your_account}</h4>
            </div>

            <div className='my-4'>
                {timer > 0 &&
                    <div className='mb-2 text-danger'>
                        {lang.you_can_try_login_again_in}
                        <b>{formatTimeSpan(timer)}</b>
                    </div>
                }

                <form ref={loginForm} onSubmit={!askEmailCode ? loginAPI : twoFactorAPI}>
                    {!askEmailCode ?
                        <>
                            <fieldset>
                                <CustomInput name="username" variant="cloud" img={importer.ic.require('user', 'svg')} placeholder={lang.username} class="mb-2" disabled={timer > 0} />
                                <CustomInput name="password" variant="cloud" minLength={8} type="password" img={importer.ic.require('password', 'svg')} placeholder={lang.password} showable={true} class="mb-2" disabled={timer > 0} />
                                <CustomCheckbox name="stay_logged_in" label={lang.stay_logged_in} />
                            </fieldset>

                            <div>
                                <CustomButton disabled={loading} loading={loading} variant="primary" type={"submit"} class="w-100 mb-2">{lang.login}</CustomButton>
                                <a onClick={() => { navigate("/forgotPassword"); }}><strong>{lang.forgot_password}</strong></a>
                            </div>
                        </>
                        :
                        <>
                            <Alert icon={false}>
                                <Title>{lang.device_not_registered}</Title>

                                <div className='my-1 w-100 justify-content-center'>
                                    <EmailRoundedIcon fontSize='large' />
                                </div>

                                <Caption>{lang.check_email_for_code}</Caption>
                            </Alert>

                            <fieldset>
                                <CustomInput name="otp" pattern="^\d+$" variant="cloud" img={importer.ic.require('password', 'svg')} placeholder={lang.code} class="mb-2" maxLength={6} />
                            </fieldset>

                            <div>
                                <CustomButton disabled={loading} loading={loading} variant="primary" type={"submit"} class="w-100 mb-2">{lang.login}</CustomButton>
                                <a onClick={() => { navigate("/forgotPassword"); }}><strong>{lang.forgot_password}</strong></a>
                            </div>
                        </>
                    }
                </form>
            </div>
        </GestCard>
    );
};
export default React.memo(LogIn);